import {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "pre_cart_v3";
const DEPOSIT = 50.0;
const LATE_CHECKOUT_FEE = 15.0;
const LATE_CHECKOUT_HOURS = 3;

function extraCost(extra, quantity, days) {
  switch (extra.price_type) {
    case "per_unit_per_day":
      return extra.price * quantity * days;
    case "per_unit_flat":
      return extra.price * quantity;
    case "flat_per_day":
      return extra.price * days;
    default:
      return extra.price;
  }
}

export function CartProvider({ children }) {
  // lines: { item_id: { item, quantity, extras: [extra_id], note } }
  const [lines, setLines] = useState({});
  const [rentalDays, setRentalDays] = useState(1);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  });
  const [customerPhone, setCustomerPhone] = useState("");
  const [lateCheckout, setLateCheckout] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [podSlug, setPodSlug] = useState("park-hill");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.lines) setLines(parsed.lines);
        if (parsed.rentalDays) setRentalDays(parsed.rentalDays);
        if (parsed.startDate) setStartDate(parsed.startDate);
        if (parsed.customerPhone) setCustomerPhone(parsed.customerPhone);
        if (parsed.lateCheckout) setLateCheckout(parsed.lateCheckout);
        if (parsed.podSlug) setPodSlug(parsed.podSlug);
      }
    } catch {}
  }, []);

  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ lines, rentalDays, startDate, customerPhone, lateCheckout, podSlug })
    );
  }, [lines, rentalDays, startDate, customerPhone, lateCheckout, podSlug]);

  const add = useCallback((item, quantity = 1, extras = [], note = "") => {
    setLines((prev) => {
      const existing = prev[item.id];
      // Merge extras (union of selections)
      const mergedExtras = Array.from(
        new Set([...(existing?.extras || []), ...extras])
      );
      // Note: keep newest non-empty note (overrides existing)
      const mergedNote = note || existing?.note || "";
      return {
        ...prev,
        [item.id]: {
          item,
          quantity: (existing?.quantity || 0) + quantity,
          extras: mergedExtras,
          note: mergedNote,
        },
      };
    });
  }, []);

  const setQty = useCallback((itemId, quantity) => {
    setLines((prev) => {
      if (quantity <= 0) {
        const next = { ...prev };
        delete next[itemId];
        return next;
      }
      if (!prev[itemId]) return prev;
      return { ...prev, [itemId]: { ...prev[itemId], quantity } };
    });
  }, []);

  const setExtras = useCallback((itemId, extras) => {
    setLines((prev) => {
      if (!prev[itemId]) return prev;
      return { ...prev, [itemId]: { ...prev[itemId], extras } };
    });
  }, []);

  const setNote = useCallback((itemId, note) => {
    setLines((prev) => {
      if (!prev[itemId]) return prev;
      return { ...prev, [itemId]: { ...prev[itemId], note } };
    });
  }, []);

  const remove = useCallback((itemId) => {
    setLines((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
  }, []);

  const clear = useCallback(() => setLines({}), []);

  const reorderFrom = useCallback((payload) => {
    // payload: { items: [{ item, quantity, extras, note }], rental_days, start_date }
    const next = {};
    for (const line of payload.items || []) {
      if (!line.item) continue;
      next[line.item.id] = {
        item: line.item,
        quantity: line.quantity || 1,
        extras: line.extras || [],
        note: line.note || "",
      };
    }
    setLines(next);
    if (payload.rental_days) setRentalDays(payload.rental_days);
    if (payload.start_date) setStartDate(payload.start_date);
    setOpen(true);
  }, []);

  const totals = useMemo(() => {
    const list = Object.values(lines);
    let subtotal = 0;
    let extrasTotal = 0;
    for (const line of list) {
      subtotal += line.item.price_per_day * line.quantity * rentalDays;
      const extrasIndex = Object.fromEntries(
        (line.item.extras || []).map((e) => [e.id, e])
      );
      for (const eid of line.extras || []) {
        const e = extrasIndex[eid];
        if (e) extrasTotal += extraCost(e, line.quantity, rentalDays);
      }
    }
    const lateCheckoutFee = lateCheckout ? LATE_CHECKOUT_FEE : 0;
    const preTax = subtotal + extrasTotal + lateCheckoutFee;
    const tax = +(preTax * 0.0815).toFixed(2);
    // Charged total — deposit is held SEPARATELY as a $50 auth-hold (not charged).
    const total = +(preTax + tax).toFixed(2);
    const count = list.reduce((s, l) => s + l.quantity, 0);
    return {
      subtotal: +subtotal.toFixed(2),
      extras: +extrasTotal.toFixed(2),
      lateCheckout: lateCheckoutFee,
      tax,
      deposit: DEPOSIT,
      total,
      count,
    };
  }, [lines, rentalDays, lateCheckout]);

  // End-date check for Sunday upsell (Sunday = 0)
  const endsOnSunday = useMemo(() => {
    const d = new Date(startDate + "T00:00:00");
    d.setDate(d.getDate() + Math.max(0, rentalDays - 1));
    return d.getDay() === 0;
  }, [startDate, rentalDays]);

  const lineCost = useCallback(
    (line) => {
      const base = line.item.price_per_day * line.quantity * rentalDays;
      const extrasIndex = Object.fromEntries(
        (line.item.extras || []).map((e) => [e.id, e])
      );
      const ex = (line.extras || []).reduce((s, eid) => {
        const e = extrasIndex[eid];
        return e ? s + extraCost(e, line.quantity, rentalDays) : s;
      }, 0);
      return { base, extras: ex, total: base + ex };
    },
    [rentalDays]
  );

  return (
    <CartContext.Provider
      value={{
        lines,
        list: Object.values(lines),
        rentalDays,
        setRentalDays,
        startDate,
        setStartDate,
        customerPhone,
        setCustomerPhone,
        lateCheckout,
        setLateCheckout,
        termsAccepted,
        setTermsAccepted,
        podSlug,
        setPodSlug,
        endsOnSunday,
        add,
        setQty,
        setExtras,
        setNote,
        remove,
        clear,
        reorderFrom,
        totals,
        lineCost,
        open,
        setOpen,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
